import React, {useEffect, useRef} from 'react';
import {Animated, Easing, View} from 'react-native';
import CCText from '@/Atoms/CCText';

const BASE = '#EFE9D8';

function SkeletonBlock({
  width,
  height,
  radius,
  opacity,
}: {
  width: number | string;
  height: number;
  radius: number;
  opacity: Animated.Value;
}) {
  return (
    <Animated.View
      style={{
        width: width as any,
        height,
        borderRadius: radius,
        backgroundColor: BASE,
        opacity,
      }}
    />
  );
}

export default function InterClubBoardSkeleton({
  count,
  elevated = true,
}: {
  count: number;
  elevated?: boolean;
}) {
  const opacity = useRef(new Animated.Value(0.45)).current;

  useEffect(() => {
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 0.9,
          duration: 650,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0.45,
          duration: 650,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    pulse.start();
    return () => pulse.stop();
  }, [opacity]);

  const rowCount = Math.max(1, Math.min(count, 5));

  return (
    <View
      testID="inter-club-board-skeleton"
      style={{
        width: '100%',
        alignSelf: 'stretch',
        backgroundColor: '#FFFFFF',
        borderRadius: 22,
        paddingHorizontal: 18,
        paddingTop: 20,
        paddingBottom: 12,
        shadowColor: '#4D3F37',
        shadowOffset: {width: 0, height: 5},
        shadowOpacity: elevated ? 0.08 : 0,
        shadowRadius: elevated ? 16 : 0,
        elevation: elevated ? 3 : 0,
      }}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 14,
          marginBottom: 18,
        }}>
        <SkeletonBlock width={50} height={50} radius={18} opacity={opacity} />
        <View style={{flex: 1}}>
          <CCText style={{fontSize: 19, fontWeight: '800', color: '#262322'}}>
            Top Performing Clubs
          </CCText>
          <CCText
            style={{
              fontSize: 12.5,
              fontWeight: '700',
              color: '#4D3F37',
              marginTop: 2,
            }}>
            Club leaderboard
          </CCText>
          <CCText style={{fontSize: 11.5, color: '#888280', marginTop: 2}}>
            Total points earned by every club member
          </CCText>
        </View>
      </View>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          paddingHorizontal: 12,
          marginBottom: 8,
        }}>
        <SkeletonBlock width={44} height={9} radius={4} opacity={opacity} />
        <SkeletonBlock width={45} height={9} radius={4} opacity={opacity} />
      </View>
      {Array.from({length: rowCount}).map((_, index) => (
        <View
          key={index}
          testID="inter-club-board-skeleton-row"
          style={{
            minHeight: 72,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 11,
            paddingVertical: 8,
            paddingHorizontal: 11,
            borderRadius: 16,
            backgroundColor: index === 0 ? '#FFF8E6' : '#F8F6F1',
            marginBottom: 6,
          }}>
          <SkeletonBlock width={30} height={30} radius={15} opacity={opacity} />
          <SkeletonBlock width={42} height={42} radius={11} opacity={opacity} />
          <View style={{flex: 1, gap: 7}}>
            <SkeletonBlock
              width="62%"
              height={12}
              radius={5}
              opacity={opacity}
            />
            <SkeletonBlock
              width="38%"
              height={9}
              radius={5}
              opacity={opacity}
            />
          </View>
          <SkeletonBlock width={54} height={24} radius={12} opacity={opacity} />
        </View>
      ))}
    </View>
  );
}
